import { SavedAddress } from './edge-api-types';
import { QuoteAddress } from './basket-types';
import { CompanyStatus, CustomsKeys } from './customs';

// Canonical meta-key spelling on the wire (PascalCase) as written by the
// customer account UI and other consumers. ui-checkout must read/write the
// same keys to interop with stored data.
export const AddressMetaKeys = {
  IOSS: 'IossNumber',
  EORI: 'EoriNumber',
  VAT_NUMBER: 'VatNumber',
  VAT_STATUS: 'VatStatus',
  TAX_ID: 'TaxId'
} as const;

// Map of PascalCase wire keys to the form-field names used inside ui-checkout.
// Address-level fields land on QuoteAddress; IOSS lands on the Customs section.
export const AddressLevelTaxMetaToField: ReadonlyArray<{ meta: string; field: string }> = [
  { meta: AddressMetaKeys.EORI, field: CustomsKeys.Address.EORI },
  { meta: AddressMetaKeys.VAT_NUMBER, field: CustomsKeys.Address.VATNUMBER },
  { meta: AddressMetaKeys.VAT_STATUS, field: CustomsKeys.Address.TAXSTATUS },
  { meta: AddressMetaKeys.TAX_ID, field: CustomsKeys.Address.TAXID }
];

const TaxMetaKeySet: ReadonlySet<string> = new Set([
  AddressMetaKeys.IOSS,
  AddressMetaKeys.EORI,
  AddressMetaKeys.VAT_NUMBER,
  AddressMetaKeys.VAT_STATUS,
  AddressMetaKeys.TAX_ID
]);

export type AddressMeta = SavedAddress['meta'];

const isNonEmpty = (value: unknown): value is string => typeof value === 'string' && value.trim().length > 0;

/**
 * `VatStatus` on the wire uses a different value scheme than ui-checkout's
 * `companyStatus` form field. The customer account UI writes values like
 * `Registered` / `NotRegistered` / `Individual`; ui-checkout's form uses
 * `business-registered` / `business-not-registered` / `individual`. The
 * mapping is the same one the backend reorder flow already applies (see
 * `MapVatStatus` in edge-order-process-api ReOrderApiResponse.cs).
 *
 * If we don't translate at the boundary, the requirement engine never matches
 * `companyStatus === 'business-registered'`, so `companyTaxNumber.requiredIf`
 * never fires and the VAT input never renders — even though the basket
 * payload looks superficially correct.
 */
const VAT_STATUS_WIRE_TO_FORM: Record<string, string> = {
  Registered: CompanyStatus.REGISTERED,
  NotRegistered: CompanyStatus.NOTREGISTERED,
  Individual: CompanyStatus.PERSONAL,
  'Personal Individual': CompanyStatus.PERSONAL,
  'Company - Non VAT Registered': CompanyStatus.NOTREGISTERED,
  // Backend `MapVatStatus` (edge-order-process-api ReOrderApiResponse.cs)
  // historically maps this legacy stored label to NotRegistered, which
  // contradicts the label's plain meaning. ui-checkout deliberately maps to
  // the semantically correct REGISTERED here so VAT inputs render and tax
  // numbers prefill correctly for saved addresses carrying the legacy label.
  // Backend alignment tracked alongside the saved-address tax-meta follow-ups
  // in docs/buy-again-route.md.
  'Company - VAT Registered': CompanyStatus.REGISTERED
};

const VAT_STATUS_FORM_TO_WIRE: Record<string, string> = {
  [CompanyStatus.REGISTERED]: 'Registered',
  [CompanyStatus.NOTREGISTERED]: 'NotRegistered',
  [CompanyStatus.PERSONAL]: 'Individual'
};

/**
 * Translates a wire `VatStatus` value into the kebab-case form value used by
 * ui-checkout. Pass-through if the value already matches a known form value
 * (defensive — handles consumers that already write in the form scheme).
 * Returns undefined for empty/missing/unrecognised values so we never
 * introduce an invalid `companyStatus` into the basket.
 */
export const translateVatStatusFromMeta = (value: string | undefined): string | undefined => {
  if (!isNonEmpty(value)) return undefined;
  if (VAT_STATUS_WIRE_TO_FORM[value]) return VAT_STATUS_WIRE_TO_FORM[value];
  // Already a known form value (some consumers write the form-scheme directly)
  if (value === CompanyStatus.REGISTERED || value === CompanyStatus.NOTREGISTERED || value === CompanyStatus.PERSONAL) {
    return value;
  }
  return undefined;
};

/**
 * Translates a ui-checkout form `companyStatus` value into the canonical
 * PascalCase wire value. Used when persisting tax meta back to the saved
 * address so other consumers (notably the customer account UI) keep seeing
 * the values they expect.
 */
export const translateVatStatusToMeta = (value: string | undefined): string | undefined => {
  if (!isNonEmpty(value)) return undefined;
  return VAT_STATUS_FORM_TO_WIRE[value] ?? value;
};

/**
 * Returns address-level form-field updates derived from a saved address's
 * meta dictionary. Only non-empty values are included so prefill never clears
 * a field the user already populated.
 */
export const readAddressTaxFieldsFromMeta = (meta: AddressMeta): Partial<QuoteAddress> => {
  if (!meta) return {};

  const updates: Record<string, string> = {};
  for (const { meta: key, field } of AddressLevelTaxMetaToField) {
    const rawValue = meta[key];
    if (!isNonEmpty(rawValue)) continue;

    if (key === AddressMetaKeys.VAT_STATUS) {
      const translated = translateVatStatusFromMeta(rawValue);
      if (translated) updates[field] = translated;
      continue;
    }

    updates[field] = rawValue;
  }
  return updates as Partial<QuoteAddress>;
};

/**
 * Returns the IOSS number stored on an origin saved address's meta, or
 * undefined if missing/empty. IOSS is only ever read from the sender side.
 */
export const readIossFromOriginMeta = (meta: AddressMeta): string | undefined => {
  if (!meta) return undefined;
  const value = meta[AddressMetaKeys.IOSS];
  return isNonEmpty(value) ? value : undefined;
};

type TaxFieldSnapshot = {
  ioss?: string;
  eori?: string;
  companyTaxNumber?: string;
  companyStatus?: string;
  taxId?: string;
};

/**
 * Builds the meta dictionary to send on a saved-address upsert. The backend
 * upsert overwrites the entire meta dict, so any existing non-tax keys
 * (e.g. `type`, `deliveryPointSuffix`, `id`) must be preserved verbatim.
 * Tax keys are overwritten when the form value is non-empty.
 */
export const buildSavedAddressMetaForUpsert = (
  existingMeta: AddressMeta,
  values: TaxFieldSnapshot,
  options: { includeIoss: boolean }
): Record<string, string> => {
  const merged: Record<string, string> = {};

  if (existingMeta) {
    for (const [key, value] of Object.entries(existingMeta)) {
      if (isNonEmpty(value)) {
        merged[key] = value;
      }
    }
  }

  const writeIfPresent = (metaKey: string, value: string | undefined) => {
    if (isNonEmpty(value)) {
      merged[metaKey] = value;
    } else {
      delete merged[metaKey];
    }
  };

  writeIfPresent(AddressMetaKeys.EORI, values.eori);
  writeIfPresent(AddressMetaKeys.VAT_NUMBER, values.companyTaxNumber);

  // `createBasketItem` initialises every Origin/Destination with
  // `companyStatus: 'individual'` regardless of user intent. Propagating that
  // default into the saved-address meta would silently add VatStatus to
  // address-book entries the user never touched. Only write VatStatus when
  // either the saved record already has one (we're updating it) or the user
  // has moved off the default (intentional business selection).
  const existingVatStatus = existingMeta?.[AddressMetaKeys.VAT_STATUS];
  const savedRecordAlreadyHadVatStatus = typeof existingVatStatus === 'string' && isNonEmpty(existingVatStatus);
  const userMovedOffDefault = isNonEmpty(values.companyStatus) && values.companyStatus !== CompanyStatus.PERSONAL;
  if (savedRecordAlreadyHadVatStatus || userMovedOffDefault) {
    writeIfPresent(AddressMetaKeys.VAT_STATUS, translateVatStatusToMeta(values.companyStatus));
  }

  writeIfPresent(AddressMetaKeys.TAX_ID, values.taxId);
  if (options.includeIoss) {
    writeIfPresent(AddressMetaKeys.IOSS, values.ioss);
  }

  return merged;
};

/**
 * The tax meta keys we own. Useful for diffing existing meta against form
 * state to decide whether anything actually changed.
 */
export const isTaxMetaKey = (key: string): boolean => TaxMetaKeySet.has(key);

/**
 * Fields whose change indicates the user has edited the structural address
 * away from the saved version. If any of these differ from the saved record,
 * the parcel is no longer "using" that saved address for tax-meta save-back.
 */
const AddressStructuralFields = [
  'property',
  'street',
  'town',
  'postcode',
  'county',
  'locality',
  'organisation'
] as const;

const normalise = (value: string | undefined | null): string => (value ?? '').trim();

/**
 * True when the QuoteAddress's structural fields all match the saved
 * address's structural fields, where "match" is asymmetric: a structural
 * field that the basket doesn't carry (empty/undefined) is not counted as
 * a mismatch, because the basket origin omits certain saved-address fields
 * by design — `useAutoApplySavedAddress` doesn't copy `locality`, and the
 * destination saved-address picker explicitly clears it. Only a basket
 * field that *has* a value and differs from the saved one counts as a
 * user edit.
 */
export const isStructurallyEqualToSavedAddress = (
  address: QuoteAddress | undefined,
  saved: SavedAddress | undefined
): boolean => {
  if (!address || !saved) return false;
  for (const field of AddressStructuralFields) {
    const basketValue = normalise(address[field] as string | undefined);
    if (!basketValue) continue;
    const savedValue = normalise(saved[field as keyof SavedAddress] as string | undefined);
    if (basketValue !== savedValue) return false;
  }
  return true;
};

/**
 * True when any of the tax meta values produced from the current form
 * actually differ from what's already stored on the saved address. Used to
 * skip no-op upserts.
 */
export const taxMetaHasChanges = (existingMeta: AddressMeta, nextMeta: Record<string, string>): boolean => {
  const before = existingMeta ?? {};
  const metaKeys = Object.keys(AddressMetaKeys).map((k) => AddressMetaKeys[k as keyof typeof AddressMetaKeys]);
  for (const key of metaKeys) {
    const beforeValue = isNonEmpty(before[key]) ? String(before[key]) : '';
    const afterValue = isNonEmpty(nextMeta[key]) ? nextMeta[key] : '';
    if (beforeValue !== afterValue) {
      return true;
    }
  }
  return false;
};
